import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Dental ToothActionsPanel — display-only side panel inside the tooth
// chart. Owns NO execution logic.
//   "+ Add diagnosis" → sdk.openWidget('diagnosis', { anatomicalRef }, {
//                       extras: <ToothDiagnosisExtras />,
//                       composePayload: applySurfacePrefix })
//   "+ Add procedure" → sdk.openWidget('procedure', { prefill }, {
//                       placement: 'chart-pane',
//                       requestId: <UUID> })
//
// Phase 3 extension — diagnosis rows are also DROP TARGETS for procedure
// drag payloads. Dropping a procedure on a specific diagnosis row links
// the new sitting to THAT exact diagnosis (instead of the chart-pane's
// default behaviour of picking the latest diagnosis on the tooth).
//
// Sibling helpers and the legacy `commonDiagnoses` list still live
// under `src/components/procedures/widget/dental/` — only this panel
// and the `ToothChartContainer` moved into the chart plugin folder.
import {
  composeContentWithSurfaces,
  parseSurfacesFromContent,
} from '../../../components/procedures/widget/dental/parseSurfacesFromContent';
import { useWidgetSDK, useChartDropTarget } from '../../../widgets';
import ToothDiagnosisExtras from './extensions/ToothDiagnosisExtras';

/**
 * ToothActionsPanel.
 *
 * Renders the read-only timeline for the selected tooth(s) — existing
 * diagnoses (with "→ Plan procedure for this" CTAs), procedures, and
 * legacy plain notes — plus two execution entry points:
 *
 *   "+ Add diagnosis"  → DiagnosisWidget (right rail / overlay)
 *   "+ Add procedure"  → ProcedureWidget (right rail) via onPlanProcedure
 *
 * The inline diagnosis form, inline procedure picker, and the inline
 * submit logic that previously lived in this file were removed when the
 * Widget Library MVP shipped — see the approved plan at
 * ~/.claude/plans/context-we-are-redesigning-cosmic-pebble.md (PR 4).
 *
 * Props (unchanged so the chart and any future caller keep working):
 *   tokens            — string[] of FDI / Q* / FM (with optional gum: prefix
 *                        and optional `-M`/`-D`/`-B`/`-L`/`-O` surface suffix)
 *   visit             — visit row (patient_id, visit_id required)
 *   viewer            — { memberId, role }
 *   procedureEntries  — sittings on these tokens (read-only timeline)
 *   noteEntries       — note + diagnosis meta entries on these tokens
 *   onAdded           — () => void; refresh signal for parent
 *   onOptimisticAdd   — (savedEntry) => void; instant local insert hook
 *   onAfterSave       — ({ kind, surface }) => void; surface auto-advance hook
 *   onPlanProcedure   — ({ diagnosisMetaId, tokens, summary }) => void;
 *                        parent dispatches the legacy half-disc prefill event
 *                        and minimises the chart
 *   onClose           — () => void
 *   isDark            — boolean
 */

const SURFACE_LABEL = {
  M: 'mesial', D: 'distal', B: 'buccal', L: 'lingual', O: 'occlusal',
};

/**
 * DiagnosisDropRow — thin `<li>` wrapper that registers each
 * diagnosis row as a drop target for procedure drag payloads (Phase 3
 * summary extension). The row body is left to the caller; this
 * component only owns the drop binding + the `arogya-summary-drop-row`
 * class so the data-drop-active CSS rule in ToothChartContainer
 * highlights the row.
 *
 * Phase 7 — when a procedure is being dragged AND this row is hovered,
 * render an inline "Links to:" chip showing the diagnosis text the
 * procedure will be tied to. Surfaces the power-user feature (drop on
 * specific diagnosis → precise linkage) so new dentists discover it.
 *
 * Hook can't be called inside the parent's `.map(...)` body, so this
 * sub-component exists to give each row its own hook instance.
 */
function DiagnosisDropRow({
  className, anatomicalRef, onDropProcedure, hoverLinkLabel, children,
}) {
  const { isOver, ...dropProps } = useChartDropTarget({
    accepts: ['procedure'],
    anatomicalRef,
    onDrop: onDropProcedure,
  });
  return (
    <li
      className={[className, 'arogya-summary-drop-row'].filter(Boolean).join(' ')}
      {...dropProps}
    >
      {isOver && hoverLinkLabel && (
        <div
          className="mb-1 inline-block px-2 py-0.5 text-[10px] font-semibold rounded-md border border-teal-500 bg-teal-100 dark:bg-teal-900/50 dark:border-teal-600 text-teal-900 dark:text-teal-100 shadow-sm"
          aria-live="polite"
        >
          → Links to: {hoverLinkLabel}
        </div>
      )}
      {children}
    </li>
  );
}

function _formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}
function _stripPrefix(t) { return t?.startsWith('gum:') ? t.slice(4) : t; }
function _isGum(t) { return t?.startsWith('gum:'); }
function _splitSurface(t) {
  const stripped = _stripPrefix(t || '');
  const m = /^([1-4][1-8]|[5-8][1-5])(-[MDBLO])?$/.exec(stripped);
  if (!m) return { tooth: stripped, surface: null };
  return { tooth: m[1], surface: m[2] ? m[2].slice(1) : null };
}
function _isQuadrantOrFm(token) {
  const t = _stripPrefix(token);
  return /^Q[1-4]$/.test(t) || t === 'FM';
}
function _tokenLabel(token) {
  const t = _stripPrefix(token);
  if (t === 'FM') return 'Full mouth';
  if (/^Q[1-4]$/.test(t)) return t;
  const { tooth, surface } = _splitSurface(token);
  if (surface) return `#${tooth} ${SURFACE_LABEL[surface]}`;
  return `#${tooth}`;
}

export default function ToothActionsPanel({
  tokens = [],
  visit,
  viewer: _viewer,
  procedureEntries = [],
  noteEntries = [],
  onAdded,
  onOptimisticAdd,
  onAfterSave,
  onPlanProcedure,
  onClose,
  isDark = false,
}) {
  const tokenList = useMemo(
    () => (Array.isArray(tokens) ? tokens.filter(Boolean) : []),
    [tokens],
  );
  const isMulti = tokenList.length > 1;
  const allGum = tokenList.length > 0 && tokenList.every(_isGum);
  const primaryToken = tokenList[0];
  const { surface: primarySurface } = _splitSurface(primaryToken);

  // Full-mouth scope is "active" when EITHER an explicit FM token sits
  // in the selection OR all four quadrants are selected (matching the
  // chart container's fmActive rule). Keeps the panel self-contained
  // — no new prop needed from the parent.
  const fmActive = useMemo(() => {
    if (tokenList.length === 0) return false;
    const stripped = tokenList.map((t) => (
      typeof t === 'string' && t.startsWith('gum:') ? t.slice(4) : t
    ));
    if (stripped.includes('FM')) return true;
    const qs = stripped.filter((t) => /^Q[1-4]$/.test(t));
    return qs.length === 4 && new Set(qs).size === 4;
  }, [tokenList]);

  // Submit-side consolidation — when FM scope is active, every API
  // call writes `tooth_numbers: ["FM"]` instead of four quadrant
  // tokens, so the right-side findings list renders ONE "Full mouth"
  // row instead of four Q rows. The data layer already supports FM
  // end-to-end (tokenToHumanLabel, SCOPE_META.fm) — no projection
  // change needed.
  const submitTokens = useMemo(
    () => (fmActive ? ['FM'] : tokenList),
    [fmActive, tokenList],
  );

  const sdk = useWidgetSDK();

  const diagnosisEntries = useMemo(
    () => noteEntries.filter((n) => (n.kind || '').toLowerCase() === 'diagnosis'),
    [noteEntries],
  );
  const plainNoteEntries = useMemo(
    () => noteEntries.filter((n) => (n.kind || 'note').toLowerCase() !== 'diagnosis'),
    [noteEntries],
  );

  // ── Header label ─────────────────────────────────────────────────
  const headerLabel = useMemo(() => {
    if (tokenList.length === 0) return 'No selection';
    if (fmActive) return 'Full mouth';
    if (tokenList.length === 1) {
      const t = primaryToken;
      const stripped = _stripPrefix(t);
      const prefix = _isGum(t) ? 'Gum @ ' : '';
      if (/^Q[1-4]$/.test(stripped)) return `${prefix}Quadrant ${stripped.slice(1)}`;
      return `${prefix}${_tokenLabel(t)}`;
    }
    const labels = tokenList.map(_tokenLabel).join(', ');
    return `${tokenList.length} ${allGum ? 'gum sites' : 'sites'}: ${labels}`;
  }, [tokenList, primaryToken, allGum, fmActive]);

  // ── DiagnosisWidget delegation ────────────────────────────────────
  // The widget owns the diagnosis form (severity + textarea + Save +
  // extras). Phase 2.B restores dental-specific UX via `opts.extras`
  // (common-diagnosis chips + surface picker) and `opts.composePayload`
  // (apply surface prefix `[M,O] ...` before save). After a successful
  // save the widget dispatches `arogya:widgets:diagnosis-saved` so we
  // can preserve the chart's instant-feedback UX (optimistic insert +
  // surface auto-advance) AND render the inline ✓ banner (Phase 2.B.8).
  const allowSurfaceEdit = !isMulti && !allGum && !_isQuadrantOrFm(primaryToken);
  const openDiagnosisWidget = useCallback(() => {
    if (!visit?.visit_id || tokenList.length === 0) return;
    sdk.openWidget(
      'diagnosis',
      {
        visitId: visit.visit_id,
        anatomicalRef: {
          kind: 'dental-tooth-surface',
          payload: { tokens: submitTokens },
          summary: headerLabel,
        },
      },
      {
        source: 'chart',
        extras: <ToothDiagnosisExtras showSurfaces={allowSurfaceEdit} />,
        composePayload: ({ content, severity, surfaces }) => {
          const trimmed = String(content || '').trim();
          const surfList = Array.isArray(surfaces) ? surfaces : [];
          // Surface prefix only meaningful for single-tooth selections.
          // Multi-token / gum / quadrant saves skip composition.
          const composed = allowSurfaceEdit && surfList.length > 0
            ? composeContentWithSurfaces(surfList, trimmed)
            : trimmed;
          return { content: composed, severity };
        },
      },
    );
  }, [sdk, visit, tokenList, submitTokens, headerLabel, allowSurfaceEdit]);

  // Phase 2.B.8 — inline "✓ Diagnosis saved" banner state. The widget
  // closes on save; this panel surfaces the confirmation where the
  // dentist last clicked, matching the pre-MVP feedback shape.
  const [savedBanner, setSavedBanner] = useState(null);
  const bannerTimerRef = useRef(null);

  useEffect(() => {
    if (!visit?.visit_id) return undefined;
    const visitId = visit.visit_id;
    const onSaved = (e) => {
      const detail = e?.detail || {};
      if (detail.visitId && detail.visitId !== visitId) return;
      // Only respond to dental-tooth-surface diagnoses opened from this
      // chart — other specialties / other panels are out of scope here.
      const ref = detail.anatomicalRef;
      if (!ref || ref.kind !== 'dental-tooth-surface') return;

      const result = detail.result;
      const severity = detail.severity;
      const body = detail.content || result?.content || '';
      const surfacesArr = Array.isArray(detail.surfaces) ? detail.surfaces : [];
      const savedTokens = (ref.payload && Array.isArray(ref.payload.tokens))
        ? ref.payload.tokens
        : tokenList;

      if (result && typeof onOptimisticAdd === 'function') {
        const optimisticRow = {
          ...result,
          visit_id: result.visit_id || visitId,
          meta_type: result.meta_type || 'NOTE',
          kind: result.kind || 'diagnosis',
          severity: result.severity || severity,
          tooth_numbers: result.tooth_numbers || savedTokens,
          content: result.content || body,
          added_by_name: result.added_by_name || _viewer?.name || 'You',
          created_at: result.created_at || new Date().toISOString(),
        };
        onOptimisticAdd(optimisticRow);
      }

      // eslint-disable-next-line no-console
      console.info(
        '[ToothChart] diagnosis saved via widget tokens=%d severity=%s server_kind=%s server_meta_id=%s',
        savedTokens.length, severity || 'MISSING',
        result?.kind || 'MISSING',
        result?.meta_id || 'MISSING',
      );

      // Inline saved banner — derive a human-readable summary
      // including surface scope when present.
      const surfacesSummary = surfacesArr.length > 0 ? ` on ${surfacesArr.join(', ')}` : '';
      setSavedBanner({
        text: `${body || 'Diagnosis'} (${severity || 'unspecified'})${surfacesSummary}`,
        count: savedTokens.length,
        at: new Date().toISOString(),
      });
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = setTimeout(() => setSavedBanner(null), 6000);

      onAdded?.();
      // Surface auto-advance: M → D → B → L → O after a single-surface save.
      onAfterSave?.({ kind: 'diagnosis', surface: primarySurface });
    };
    window.addEventListener('arogya:widgets:diagnosis-saved', onSaved);
    return () => {
      window.removeEventListener('arogya:widgets:diagnosis-saved', onSaved);
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, [visit, tokenList, onOptimisticAdd, onAdded, onAfterSave, primarySurface, _viewer]);

  // ── ProcedureWidget delegation ────────────────────────────────────
  // Phase 2.C — the procedure picker mounts INLINE in the chart pane
  // (via `placement: 'chart-pane'`). The chart's SDK subscriber in
  // ToothChartContainer renders <ChartPaneProcedurePicker /> into the
  // right side region. The right-edge rail does NOT expand for chart-
  // sourced procedure opens — eye stays in the chart pane.
  //
  // Phase 2.A.2 — legacy event-dispatch fallback removed; the SDK is
  // the single source of truth for chart-driven procedure picks. The
  // upstream `onPlanProcedure` prop is still called (V2 chart minimize
  // hook, ToothFindingsSummary's external listener) but is now purely
  // for parent coordination — the prefill flows through the SDK.
  //
  // Phase 2.A.4 — every activation carries a UUID so rapid successive
  // clicks defeat the no-op risk (last-write-wins, fresh re-render).
  const planProcedureFor = useCallback((diagnosisMetaId, baseSummary, dragPayload = null) => {
    if (!visit?.visit_id || tokenList.length === 0) return;
    const toothFdis = tokenList
      .map((t) => _splitSurface(t).tooth)
      .filter((t) => t && !/^Q[1-4]$|^FM$/.test(t))
      .filter((t, i, a) => a.indexOf(t) === i)
      .sort();
    const toothLabel = toothFdis.length === 0
      ? ''
      : toothFdis.length === 1
        ? `Tooth #${toothFdis[0]}`
        : `Teeth #${toothFdis.join(', #')}`;
    const composedSummary = toothLabel && baseSummary
      ? `${toothLabel} · ${baseSummary}`
      : (toothLabel || baseSummary || null);

    const requestId = (
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    );

    const payload = {
      diagnosisMetaId: diagnosisMetaId || null,
      tokens: submitTokens,
      summary: composedSummary,
      // Phase 3 extension — when triggered via drag-drop onto a
      // diagnosis row, the drag payload carries the procedure
      // template. Forward it so the picker mounts pre-selected.
      procedureId:   dragPayload?.procedureId   || null,
      procedureName: dragPayload?.procedureName || null,
      requestId,
    };

    // Inform the parent so it can run side effects (minimize chart in
    // legacy V2 paths, etc.). The parent no longer drives the prefill
    // flow itself — that's the SDK's job below.
    if (typeof onPlanProcedure === 'function') {
      onPlanProcedure(payload);
    }

    sdk.openWidget(
      'procedure',
      {
        visitId: visit.visit_id,
        anatomicalRef: {
          kind: 'dental-tooth-surface',
          payload: { tokens: submitTokens },
          summary: headerLabel,
        },
        prefill: payload,
      },
      {
        source: dragPayload ? 'drag-drop-summary' : 'chart',
        // Render the picker INSIDE the chart pane's right region
        // instead of expanding the right-edge rail. Eye stays in the
        // chart pane.
        placement: 'chart-pane',
        requestId,
      },
    );
  }, [sdk, visit, tokenList, submitTokens, headerLabel, onPlanProcedure]);

  const openProcedurePicker = useCallback(() => {
    const latest = diagnosisEntries[0] || null;
    const parsedLatest = latest
      ? parseSurfacesFromContent(latest.content || '')
      : { surfaces: [], rest: '' };
    const baseSummary = latest ? (parsedLatest.rest || latest.content) : null;
    planProcedureFor(latest?.meta_id || null, baseSummary);
  }, [diagnosisEntries, planProcedureFor]);

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div
      className={[
        'rounded-md border p-3 flex flex-col gap-3',
        isDark
          ? 'bg-slate-900 border-slate-700 text-slate-100'
          : 'bg-white border-slate-300 text-slate-900',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{headerLabel}</h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-0.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            aria-label="Close panel"
          >
            ✕
          </button>
        )}
      </div>

      {(isMulti || fmActive) && (
        <div className="text-[11px] italic text-slate-500 dark:text-slate-400">
          {fmActive
            ? 'Bulk-add mode: one entry will be saved for the full mouth.'
            : (
              <>
                Bulk-add mode: one entry will be saved across all
                {' '}{tokenList.length} {allGum ? 'gum sites' : 'sites'}.
              </>
            )}
        </div>
      )}

      {/* ── Existing diagnoses + Plan-procedure CTA (single-token only) ─── */}
      {!isMulti && diagnosisEntries.length > 0 && (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide opacity-70 mb-1">
            Diagnoses ({diagnosisEntries.length})
          </h4>
          <ul className="space-y-1">
            {diagnosisEntries.map((d, i) => {
              const parsed = parseSurfacesFromContent(d.content || '');
              const linkedSittings = (procedureEntries || []).filter(
                (s) => s && s.linked_diagnosis_metadata_id === d.meta_id,
              );
              const liveLinks = linkedSittings.filter(
                (s) => !['cancelled', 'voided', 'rejected'].includes(
                  (s.status || '').toLowerCase(),
                ),
              );
              const hasCompletedLink = liveLinks.some(
                (s) => ['completed', 'paid'].includes(
                  (s.status || '').toLowerCase(),
                ),
              );
              const hasOpenPlannedLink = liveLinks.some(
                (s) => !['completed', 'paid'].includes(
                  (s.status || '').toLowerCase(),
                ),
              );
              const isResolved = hasCompletedLink && !hasOpenPlannedLink;
              const rowAnatomicalRef = {
                kind: 'dental-tooth-surface',
                payload: {
                  tokens: Array.isArray(d.tooth_numbers) && d.tooth_numbers.length > 0
                    ? d.tooth_numbers
                    : tokenList,
                  diagnosisMetaId: d.meta_id || null,
                },
                summary: parsed.rest || d.content || headerLabel,
              };
              const dxLinkLabel = (parsed.rest || d.content || '').trim() || 'this diagnosis';
              return (
              <DiagnosisDropRow
                key={d.meta_id || `${d.created_at}-${i}`}
                className={[
                  'rounded-md border px-2 py-1.5',
                  isResolved
                    ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30'
                    : 'border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30',
                ].join(' ')}
                anatomicalRef={rowAnatomicalRef}
                hoverLinkLabel={dxLinkLabel}
                onDropProcedure={(payload) => planProcedureFor(
                  d.meta_id, parsed.rest || d.content || '', payload,
                )}
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className={[
                    'text-xs font-medium',
                    isResolved ? 'line-through opacity-70' : '',
                  ].join(' ').trim()}>
                    {parsed.rest || d.content}
                    {parsed.surfaces.length > 0 && (
                      <span className="ml-1 text-[10px] opacity-80">
                        · {parsed.surfaces.join(', ')}
                      </span>
                    )}
                    {d.severity && (
                      <span className="ml-1 text-[10px] opacity-70">({d.severity})</span>
                    )}
                  </span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                    {d.added_by_name || 'Doctor'}
                    {' · '}
                    {_formatDate(d.created_at)}
                  </span>
                </div>

                {linkedSittings.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {linkedSittings.map((s) => {
                      const status = (s.status || '').toLowerCase();
                      const isDone = ['completed', 'paid'].includes(status);
                      const isCancelled = ['cancelled', 'voided', 'rejected'].includes(status);
                      const dotColor = isCancelled
                        ? '#94a3b8'
                        : isDone
                          ? '#16a34a'
                          : '#2563eb';
                      const statusLabel = isCancelled
                        ? 'cancelled'
                        : isDone
                          ? 'completed'
                          : (status || 'planned');
                      return (
                        <li
                          key={s.sitting_id}
                          className="flex items-baseline gap-1.5 text-[11px] text-slate-700 dark:text-slate-200"
                        >
                          <span
                            aria-hidden="true"
                            className="inline-block rounded-full"
                            style={{
                              width: 6, height: 6, backgroundColor: dotColor,
                            }}
                          />
                          <span className="truncate" style={{ maxWidth: '14ch' }}>
                            {s.procedure_name || 'Procedure'}
                          </span>
                          {s.assigned_to_name && (
                            <span
                              className="text-[10px] text-slate-500 dark:text-slate-400 truncate"
                              style={{ maxWidth: '12ch' }}
                              title={s.assigned_to_name}
                            >
                              · {s.assigned_to_name}
                            </span>
                          )}
                          <span className={[
                            'text-[10px] uppercase tracking-wide font-semibold',
                            isCancelled
                              ? 'text-slate-500'
                              : isDone
                                ? 'text-emerald-700 dark:text-emerald-300'
                                : 'text-blue-700 dark:text-blue-300',
                          ].join(' ')}
                          >
                            · {statusLabel}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {(() => {
                  const hasActiveLink = linkedSittings.some(
                    (s) => !['cancelled', 'voided', 'rejected'].includes(
                      (s.status || '').toLowerCase(),
                    ),
                  );
                  if (hasActiveLink) {
                    return (
                      <div className="mt-1 inline-block px-2 py-0.5 text-[11px] rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                        ✓ Linked to procedure
                      </div>
                    );
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => planProcedureFor(d.meta_id, parsed.rest || d.content || '')}
                      className="mt-1 px-2 py-0.5 text-[11px] rounded-md border border-rose-400 dark:border-rose-600 bg-white dark:bg-slate-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/40"
                    >
                      → Plan procedure for this
                    </button>
                  );
                })()}
              </DiagnosisDropRow>
              );
            })}
          </ul>
        </section>
      )}

      {/* ── Existing procedures (read-only timeline, single-token only) ─── */}
      {!isMulti && procedureEntries.length > 0 && (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide opacity-70 mb-1">
            Procedures on this {_isQuadrantOrFm(primaryToken) ? 'region' : 'tooth'} ({procedureEntries.length})
          </h4>
          <ul className="space-y-1">
            {procedureEntries.map((s, i) => (
              <li
                key={s.sitting_id || `${s.plan_id}-${i}`}
                className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1.5"
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="text-xs font-medium truncate">
                    {s.procedure_name || 'Procedure'}
                  </span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                    {_formatDate(s.completed_at || s.created_at)}
                  </span>
                </div>
                {s.completer_name && (
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                    by {s.completer_name}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Existing plain notes (legacy, read-only context) ───────────── */}
      {!isMulti && plainNoteEntries.length > 0 && (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide opacity-50 mb-1">
            Legacy notes ({plainNoteEntries.length}) — read-only
          </h4>
          <ul className="space-y-1">
            {plainNoteEntries.map((n, i) => (
              <li
                key={n.meta_id || `${n.created_at}-${i}`}
                className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1 opacity-80"
              >
                <div className="text-[11px] whitespace-pre-wrap">{n.content}</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                  {n.added_by_name || 'Unknown'}
                  {' · '}
                  {_formatDate(n.created_at)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Add diagnosis CTA — delegates to DiagnosisWidget. The inline
            form was removed in PR 4 of the Widget Library MVP; the
            widget owns severity + textarea + Save + extras (common-
            diagnosis chips + surface picker passed via opts.extras in
            Phase 2.B). After save, this panel's effect picks up the
            `arogya:widgets:diagnosis-saved` event and drives the
            optimistic insert + surface auto-advance + inline banner. */}
      {tokenList.length > 0 && (
        <section className="pt-2 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-2">
          {savedBanner && (
            <div className="rounded-md border border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-700 px-2.5 py-1.5 text-xs text-emerald-800 dark:text-emerald-200">
              <div className="font-semibold">✓ Diagnosis saved</div>
              <div className="opacity-80 mt-0.5">{savedBanner.text}</div>
              <div className="text-[10px] opacity-70 mt-0.5">
                {savedBanner.count} site{savedBanner.count === 1 ? '' : 's'} updated · visible in the chart
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={openDiagnosisWidget}
            className="w-full px-3 py-2 text-xs font-semibold rounded-md text-white bg-rose-600 hover:bg-rose-700"
          >
            {fmActive
              ? '+ Add diagnosis for Full Mouth'
              : isMulti
                ? `+ Add diagnosis for ${tokenList.length} sites`
                : '+ Add diagnosis'}
          </button>

          <button
            type="button"
            onClick={openProcedurePicker}
            className="w-full px-3 py-2 text-xs font-semibold rounded-md text-white bg-teal-600 hover:bg-teal-700 flex items-center justify-center gap-1.5"
            title={
              diagnosisEntries.length > 0
                ? `Plan procedure linked to: "${(diagnosisEntries[0].content || '').slice(0, 60)}"`
                : 'Plan a procedure for the selected teeth (no diagnosis linked)'
            }
          >
            {/* Phase 6 — label reflects multi-selection count (matches
                the "+ Add diagnosis for N sites" pattern above). */}
            <span>
              {fmActive
                ? '+ Add procedure for Full Mouth'
                : isMulti
                  ? `+ Add procedure for ${tokenList.length} sites`
                  : '+ Add procedure'}
            </span>
            {diagnosisEntries.length > 0 && (
              <span className="text-[10px] font-normal opacity-90">
                · linked to latest diagnosis
              </span>
            )}
          </button>
        </section>
      )}
    </div>
  );
}
